/**
 * A onda enquanto grava, como no WhatsApp (pedido do Igor, 2026-10-30): cada
 * risquinho é o volume do microfone num instante; os novos entram pela direita
 * e andam para a esquerda. Falou alto, sobe; em silêncio, fica um tracinho.
 */
import { useEffect, useRef } from "react";

/** De quanto em quanto tempo nasce um risquinho novo. */
const PASSO_MS = 70;
const LARGURA_DO_RISCO = 3;
const ESPACO_ENTRE_RISCOS = 2;
const ALTURA_MINIMA = 3;
/** A voz falada fica longe do máximo do microfone: o ganho a traz para a faixa visível. */
const GANHO = 1.6;

/**
 * Volume (0 a 1) de uma amostra do analisador (bytes de 0 a 255, 128 = silêncio).
 * RMS com raiz e ganho: fala normal ocupa boa parte da altura, sussurro aparece.
 */
export function nivelDaAmostra(bytes) {
  if (!bytes || bytes.length === 0) return 0;
  let soma = 0;
  for (let i = 0; i < bytes.length; i += 1) {
    const v = (bytes[i] - 128) / 128;
    soma += v * v;
  }
  const rms = Math.sqrt(soma / bytes.length);
  return Math.min(1, Math.sqrt(rms) * GANHO);
}

/** Altura em px de um risquinho para o nível (nunca some: silêncio é um tracinho). */
export function alturaDoRisco(nivel, alturaTotal) {
  return Math.max(ALTURA_MINIMA, Math.round(nivel * alturaTotal));
}

function desenhar(canvas, niveis) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const escala = window.devicePixelRatio || 1;
  const largura = canvas.clientWidth;
  const altura = canvas.clientHeight;
  if (canvas.width !== Math.round(largura * escala)) canvas.width = Math.round(largura * escala);
  if (canvas.height !== Math.round(altura * escala)) canvas.height = Math.round(altura * escala);
  ctx.setTransform(escala, 0, 0, escala, 0, 0);
  ctx.clearRect(0, 0, largura, altura);
  ctx.fillStyle = getComputedStyle(canvas).color;
  const passo = LARGURA_DO_RISCO + ESPACO_ENTRE_RISCOS;
  const cabem = Math.floor(largura / passo);
  const visiveis = niveis.slice(-cabem);
  // Encostados à direita: o mais novo na ponta, o antigo saindo pela esquerda.
  const inicio = largura - visiveis.length * passo;
  visiveis.forEach((nivel, i) => {
    const h = alturaDoRisco(nivel, altura);
    const x = inicio + i * passo;
    const y = (altura - h) / 2;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x, y, LARGURA_DO_RISCO, h, LARGURA_DO_RISCO / 2);
    else ctx.rect(x, y, LARGURA_DO_RISCO, h);
    ctx.fill();
  });
}

export function OndaDaGravacao({ fluxo }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const Contexto = window.AudioContext || window.webkitAudioContext;
    if (!fluxo || !canvas || !Contexto) return undefined;
    const contexto = new Contexto();
    const analisador = contexto.createAnalyser();
    analisador.fftSize = 1024;
    const fonte = contexto.createMediaStreamSource(fluxo);
    fonte.connect(analisador);
    const amostra = new Uint8Array(analisador.fftSize);
    const niveis = [];
    const maximo = 400; // mais do que cabe em qualquer tela; o resto fica de fora
    const id = window.setInterval(() => {
      analisador.getByteTimeDomainData(amostra);
      niveis.push(nivelDaAmostra(amostra));
      if (niveis.length > maximo) niveis.shift();
      desenhar(canvas, niveis);
    }, PASSO_MS);
    return () => {
      window.clearInterval(id);
      fonte.disconnect();
      contexto.close().catch(() => undefined);
    };
  }, [fluxo]);

  return <canvas ref={canvasRef} className="onda" aria-hidden="true" />;
}
